import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowSquareOut, Copy, Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HandoverReport } from '@/pages/engineering/development/HandoverReport';
import { mfgApi } from './mfgApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 元の開発案件（2026-10-22）。
 *
 * 引き渡しで作られた設備は案件へのリンクと「予備部品（開発時の部品表）」を見せる。
 * 既にある設備も、あとから案件に紐付けられる（記録に残り、取り消せる）。
 */
export const EquipmentDevelopmentSection = ({ eq, canWrite, onChanged, testId = 'eq-dev-panel' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [copyParts, setCopyParts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [saveSnapshot, setSaveSnapshot] = useState(true);
  const [records, setRecords] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [spareBatch, setSpareBatch] = useState(null);

  /** 凍結記録はダイアログのまま印刷する（背景の画面は隠す）。 */
  const printRecord = () => {
    document.body.classList.add('printing-record');
    window.print();
    window.setTimeout(() => document.body.classList.remove('printing-record'), 500);
  };

  const refreshSpares = async () => {
    setBusy('spares');
    try {
      const r = await mfgApi.refreshSpareParts(eq.id);
      setSpareBatch(r.batch_id);
      toast.success(`案件から ${r.count} 件を取り込みました（元に戻せます）`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '取り込めませんでした');
    } finally { setBusy(''); }
  };

  const undoSpares = async () => {
    try {
      await mfgApi.undoSpareParts(spareBatch);
      setSpareBatch(null);
      toast.success('元に戻しました');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    }
  };

  const loadRecords = async () => {
    try {
      const r = await axios.get(`${API}/mfg/equipment/${eq.id}/handover-records`);
      setRecords(Array.isArray(r.data) ? r.data : []);
    } catch { setRecords([]); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRecords(); }, [eq.id, eq.handover_record_id]);

  const openRecord = async (rec) => {
    try {
      const r = await axios.get(`${API}/mfg/equipment/${eq.id}/handover-records/${rec.id}`);
      setViewing(r.data);
    } catch { toast.error('記録を開けませんでした'); }
  };

  const newVersion = async () => {
    setBusy('rec');
    try {
      await axios.post(`${API}/mfg/equipment/${eq.id}/handover-records`, { reason });
      toast.success('新しい版として保存しました（前の版は残ります）');
      setReasonOpen(false); setReason('');
      await loadRecords();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(''); }
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      axios.get(`${API}/eng/developments`, { params: { q, limit: 20 } })
        .then((r) => setRows(Array.isArray(r.data) ? r.data : (r.data?.rows || [])))
        .catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q]);

  const link = async (devId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mfg/equipment/${eq.id}/link-development`,
        { development_id: devId, copy_parts: copyParts, save_snapshot: saveSnapshot });
      setLastBatch(r.data.batch_id);
      toast.success('開発案件と紐付けました');
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '紐付けできませんでした');
    } finally { setBusy(false); }
  };

  const undo = async () => {
    try {
      await axios.post(`${API}/mfg/equipment/link-development/undo/${lastBatch}`);
      setLastBatch(null);
      toast.success('紐付けを取り消しました');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '取り消せませんでした');
    }
  };

  const copy = async (path) => {
    try {
      await navigator.clipboard.writeText(path);
      toast.success('パスをコピーしました。エクスプローラーのアドレス欄（Ctrl+L）に貼り付けて Enter');
    } catch {
      toast.error('コピーできませんでした');
    }
  };

  const spares = eq.spare_parts || [];
  const paths = eq.development_paths || [];

  return (
    <section className="rounded-lg border border-zinc-200" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
        <h3 className="text-sm font-semibold text-zinc-700">元の開発案件</h3>
        {canWrite && (
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-teal-700"
            onClick={() => setOpen(true)} data-testid={`${testId}-link-btn`}>
            <Plus size={13} className="mr-1" /> 開発案件と紐付け
          </Button>
        )}
      </div>

      <div className="px-3 py-2">
        {eq.source_development_id ? (
          <a href={`/engineering/developments/${eq.source_development_id}`}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-teal-700 hover:text-teal-900"
            data-testid={`${testId}-link`}>
            {eq.source_development_title || '開発案件を開く'} <ArrowSquareOut size={12} weight="bold" />
          </a>
        ) : (
          <p className="text-[12px] text-zinc-400" data-testid={`${testId}-empty`}>なし</p>
        )}
        {lastBatch && (
          <Button size="sm" variant="outline" className="ml-2 h-7 text-[11px]"
            onClick={undo} data-testid={`${testId}-undo`}>紐付けを取り消す</Button>
        )}
      </div>

      {/* C2 · 開発時の記録（完了報告書）の版 */}
      <div className="border-t border-zinc-100 px-3 py-2" data-testid={`${testId}-records`}>
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-zinc-500">開発時の記録（完了報告書）</p>
          {canWrite && eq.source_development_id && (
            <Button size="sm" variant="ghost" className="h-6 text-[11px] text-teal-700"
              onClick={() => setReasonOpen(true)} data-testid={`${testId}-new-version`}>
              記録を更新（v{(records[0]?.version || 0) + 1}）
            </Button>
          )}
        </div>
        {records.length === 0 ? (
          <p className="mt-1 text-[12px] text-zinc-400" data-testid={`${testId}-records-empty`}>なし</p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {records.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 text-[12px]">
                <button type="button" onClick={() => openRecord(r)}
                  className="font-semibold text-teal-700 hover:text-teal-900"
                  data-testid={`${testId}-record-${r.version}`}>
                  v{r.version} 完了報告書
                </button>
                <span className="text-[11px] text-zinc-500">
                  {(r.created_at || '').slice(0, 10)} ／ {r.created_by}
                  {r.reason ? ` ／ ${r.reason}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* D · 製作時の部品構成（凍結）— その日の名前のまま */}
      {records.length > 0 && (
        <div className="border-t border-zinc-100 px-3 py-2">
          <p className="text-[11px] font-semibold text-zinc-500">製作時の部品構成（凍結）</p>
          <p className="mt-0.5 text-[10px] text-zinc-400">
            引き渡した日の名前・図番・数量のまま。直せません。
          </p>
          <ul className="mt-1 space-y-0.5" data-testid={`${testId}-frozen-bom`}>
            {(viewing?.frozen_bom || records[0]?.frozen_bom || []).map((p, i) => (
              <li key={`${p.name}-${i}`} className="flex flex-wrap items-center gap-2 text-[12px]">
                <span className="font-mono text-[11px] text-zinc-500">{p.part_no || '—'}</span>
                <span className="text-zinc-800">{p.name}</span>
                <span className="text-zinc-500">{p.qty_per_unit}{p.uom || ''}</span>
                {p.drawing_no && <span className="text-[11px] text-zinc-500">図番 {p.drawing_no}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-zinc-100 px-3 py-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-zinc-500">予備部品（いまのマスターの値）</p>
          {canWrite && eq.source_development_id && (
            <Button size="sm" variant="ghost" className="h-6 text-[11px] text-teal-700"
              disabled={busy === 'spares'} onClick={refreshSpares}
              data-testid={`${testId}-refresh-spares`}>
              予備部品を案件から再取込
            </Button>
          )}
        </div>
        {spareBatch && (
          <Button size="sm" variant="outline" className="mt-1 h-7 text-[11px]"
            onClick={undoSpares} data-testid={`${testId}-undo-spares`}>
            再取込を元に戻す
          </Button>
        )}
        {spares.length === 0 ? (
          <p className="mt-1 text-[12px] text-zinc-400" data-testid={`${testId}-spares-empty`}>なし</p>
        ) : (
          <ul className="mt-1 space-y-0.5" data-testid={`${testId}-spares`}>
            {spares.map((p, i) => (
              <li key={p.item_id || `${p.name}-${i}`} className="flex flex-wrap items-center gap-2 text-[12px]"
                data-testid={`${testId}-spare-${i}`}>
                <span className="font-mono text-[11px] text-zinc-500">{p.sku || '—'}</span>
                <span className="text-zinc-800">{p.name}</span>
                <span className="text-zinc-500">
                  {p.qty_per_unit ? `${p.qty_per_unit}${p.uom || ''}` : (p.uom || '')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {paths.length > 0 && (
        <div className="border-t border-zinc-100 px-3 py-2">
          <p className="text-[11px] font-semibold text-zinc-500">案件の共有フォルダー</p>
          <ul className="mt-1 space-y-0.5" data-testid={`${testId}-paths`}>
            {paths.map((p, i) => (
              <li key={`${p.path}-${i}`} className="flex flex-wrap items-center gap-2 text-[12px]">
                <Badge variant="outline" className="bg-zinc-50 text-[10px] text-zinc-600">
                  {p.kind_label || 'パス'}
                </Badge>
                <span className="text-zinc-800">{p.display_name}</span>
                <button type="button" onClick={() => copy(p.path)}
                  className="inline-flex items-center gap-1 font-mono text-[11px] text-teal-700 hover:text-teal-900"
                  data-testid={`${testId}-path-${i}`}>
                  <Copy size={11} /> {p.path}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 記録を更新（v2…）の理由 */}
      <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}
          data-testid={`${testId}-version-dialog`}>
          <DialogHeader><DialogTitle>記録を更新（新しい版）</DialogTitle></DialogHeader>
          <p className="text-[12px] text-zinc-600">
            前の版はそのまま残ります。何を直したかを書いてください。
          </p>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} className="h-10"
            placeholder="例: 最終図面の版を反映" data-testid={`${testId}-version-reason`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonOpen(false)}>閉じる</Button>
            <Button onClick={newVersion} disabled={busy === 'rec'}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid={`${testId}-version-save`}>保存する</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 保存された完了報告書を読む（印刷・PDF保存はブラウザの印刷） */}
      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="print-handover max-h-[85vh] max-w-4xl overflow-y-auto" aria-describedby={undefined}
          data-testid={`${testId}-record-view`}>
          <DialogHeader>
            <DialogTitle>
              完了報告書・引継ぎ資料 v{viewing?.version}（{(viewing?.created_at || '').slice(0, 10)}）
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="text-[12px] text-zinc-800">
              <p className="text-zinc-600">
                案件: {viewing.development_title} ／ 保存: {viewing.created_by}
                {viewing.reason ? ` ／ ${viewing.reason}` : ''}
              </p>
              {/* C2 · 画面の完了報告書と同じ全8章をそのまま描く（控えは直せない） */}
              {viewing.payload
                ? <HandoverReport d={viewing.payload} frozenAt={viewing.created_at} />
                : <p className="mt-2 text-zinc-500">この版には本文が保存されていません。</p>}
              <section className="mt-4">
                <h4 className="border-b-2 border-zinc-800 pb-1 text-base font-bold text-zinc-900">
                  製作時の部品構成（凍結）
                </h4>
                <ul className="mt-2 space-y-0.5">
                  {(viewing.frozen_bom || []).map((p, i) => (
                    <li key={`${p.name}-${i}`}>
                      <span className="font-mono text-[11px] text-zinc-500">{p.part_no || '—'}</span>{' '}
                      {p.name} · {p.qty_per_unit}{p.uom || ''}
                      {p.drawing_no ? ` · 図番 ${p.drawing_no}` : ''}
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
          <DialogFooter className="print:hidden">
            <Button variant="outline" onClick={printRecord}
              data-testid={`${testId}-record-print`}>印刷 / PDF保存</Button>
            <Button variant="outline" onClick={() => setViewing(null)}>閉じる</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}
          data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>開発案件と紐付け</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-zinc-700">案件を探す</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-10"
              placeholder="案件名で検索" data-testid={`${testId}-search`} />
            <label className="flex items-center gap-1.5 text-[12px] text-zinc-700">
              <input type="checkbox" checked={copyParts}
                onChange={(e) => setCopyParts(e.target.checked)}
                data-testid={`${testId}-copy-parts`} />
              案件の部品表を「予備部品」に写す
            </label>
            <label className="flex items-center gap-1.5 text-[12px] text-zinc-700">
              <input type="checkbox" checked={saveSnapshot}
                onChange={(e) => setSaveSnapshot(e.target.checked)}
                data-testid={`${testId}-save-snapshot`} />
              この時点の記録（完了報告書）を保存する
            </label>
            <ul className="max-h-64 divide-y divide-zinc-100 overflow-y-auto rounded border border-zinc-200">
              {rows.map((d) => (
                <li key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-[12px]">
                  <span className="min-w-0 flex-1 truncate text-zinc-800">
                    {d.title || d.concept?.title || d.id}
                  </span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    disabled={busy} onClick={() => link(d.id)}
                    data-testid={`${testId}-pick-${d.id}`}>紐付ける</Button>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}
              data-testid={`${testId}-close`}>閉じる</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default EquipmentDevelopmentSection;
